import {
  Box,
  VStack,
  Avatar,
  Badge,
  Button,
  Text,
  HStack,
  Spinner,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import { useConversations, ConversationWithDetails } from '../useConversations';
import { useAuth } from '../../../../contexts/AuthContext';
import moment from 'moment';

interface ConversationSidebarProps {
  selectedConversationId?: string;
  onSelectConversation: (conversationId: string) => void;
  onNewConversation: () => void;
}

export function ConversationSidebar({
  selectedConversationId,
  onSelectConversation,
  onNewConversation,
}: ConversationSidebarProps) {
  const { currentUser } = useAuth();
  const { conversations, loading, error } = useConversations(currentUser?.uid);
  const bgColor = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const selectedBg = useColorModeValue('blue.50', 'blue.900');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const totalUnread = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);

  if (loading) {
    return (
      <Box
        w="100%"
        h="100%"
        bg={bgColor}
        borderRightWidth="1px"
        borderColor={borderColor}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        w="100%"
        h="100%"
        bg={bgColor}
        borderRightWidth="1px"
        borderColor={borderColor}
        p={4}
      >
        <Text color="red.500" fontSize="sm">
          Error loading conversations: {error}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      w="100%"
      h="100%"
      bg={bgColor}
      borderRightWidth="1px"
      borderColor={borderColor}
      display="flex"
      flexDirection="column"
    >
      {/* Header */}
      <Box p={4} borderBottomWidth="1px" borderColor={borderColor}>
        <HStack justify="space-between" mb={3}>
          <Text fontSize="lg" fontWeight="semibold">
            Messages
          </Text>
          {totalUnread > 0 && (
            <Badge colorScheme="blue" borderRadius="full" px={2} py={1}>
              {totalUnread}
            </Badge>
          )}
        </HStack>
        <Button
          colorScheme="blue"
          size="sm"
          width="100%"
          onClick={onNewConversation}
        >
          New Message
        </Button>
      </Box>

      {/* Conversation List */}
      <Box flex="1" overflowY="auto">
        {conversations.length === 0 ? (
          <Box p={4} textAlign="center">
            <Text fontSize="sm" color="gray.500" mb={1}>
              No conversations yet
            </Text>
            <Text fontSize="xs" color="gray.400">
              Start a new message to get started
            </Text>
          </Box>
        ) : (
          <VStack spacing={0} align="stretch" divider={<Divider />}>
            {conversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                isSelected={conversation.id === selectedConversationId}
                onClick={() => onSelectConversation(conversation.id)}
                hoverBg={hoverBg}
                selectedBg={selectedBg}
              />
            ))}
          </VStack>
        )}
      </Box>
    </Box>
  );
}

interface ConversationItemProps {
  conversation: ConversationWithDetails;
  isSelected: boolean;
  onClick: () => void;
  hoverBg: string;
  selectedBg: string;
}

function ConversationItem({
  conversation,
  isSelected,
  onClick,
  hoverBg,
  selectedBg,
}: ConversationItemProps) {
  const getDisplayName = () => {
    if (conversation.type === 'direct') {
      return conversation.otherUser?.name || 'Unknown User';
    }
    
    // For group chats, show name if available, otherwise show member names
    if (conversation.name) {
      return conversation.name;
    }
    
    // Show member names if available
    if (conversation.membersDetails && conversation.membersDetails.length > 0) {
      const memberNames = conversation.membersDetails
        .map((m) => m.name)
        .join(', ');
      // Truncate if too long
      return memberNames.length > 50 
        ? memberNames.substring(0, 47) + '...'
        : memberNames;
    }
    
    // Fallback to member count
    return `Group Chat (${conversation.members.length})`;
  };

  const displayName = getDisplayName();
  const displayAvatar =
    conversation.type === 'direct'
      ? conversation.otherUser?.photoURL
      : undefined;

  const lastMessageText = conversation.lastMessage
    ? conversation.lastMessage.content.length > 50
      ? conversation.lastMessage.content.substring(0, 50) + '...'
      : conversation.lastMessage.content
    : 'No messages yet';

  const lastMessageTime = conversation.lastMessage
    ? moment(conversation.lastMessage.createdAt.toDate()).calendar()
    : '';

  return (
    <Box
      p={3}
      cursor="pointer"
      bg={isSelected ? selectedBg : 'transparent'}
      _hover={{ bg: hoverBg }}
      onClick={onClick}
      transition="background-color 0.2s"
    >
      <HStack spacing={3} align="flex-start">
        <Avatar
          size="sm"
          src={displayAvatar}
          name={displayName}
        />
        <VStack align="flex-start" spacing={0} flex="1" minW={0}>
          <HStack justify="space-between" width="100%">
            <Text
              fontSize="sm"
              fontWeight={conversation.unreadCount > 0 ? 'semibold' : 'normal'}
              isTruncated
            >
              {displayName}
            </Text>
            {conversation.unreadCount > 0 && (
              <Badge colorScheme="blue" borderRadius="full" minW="20px" textAlign="center">
                {conversation.unreadCount}
              </Badge>
            )}
          </HStack>
          <HStack justify="space-between" width="100%" spacing={2}>
            <Text
              fontSize="xs"
              color="gray.500"
              isTruncated
              flex="1"
            >
              {lastMessageText}
            </Text>
            {lastMessageTime && (
              <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">
                {lastMessageTime}
              </Text>
            )}
          </HStack>
        </VStack>
      </HStack>
    </Box>
  );
}

